import { useState, useEffect, ReactElement, createRef, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Unity, useUnityContext } from "react-unity-webgl";
import { RiFullscreenLine } from "react-icons/ri";
import { jwtSelector } from "../../../common/state/selectors/authSelector";
import { isBrowserSupportingWebGL } from "../../../common/util/isBrowserSupportingWeb";
import UnsupportedWebGL from "../../../common/components/UnsupportedWebGL/UnsupportedWebGL";
import { AppDispatch } from "../../../common/state/store";
import { useTranslation } from "react-i18next";
import { setAlert } from "../../../features/alert/state/alertsSlice";
import { useParams } from "react-router-dom";
import ProgressBar from "../ProgressBar/ProgressBar";

const viewerFolder = "/ProjectPreviewWebGL/";

// Story 13.5: Unity WebGL error suppression
// Unity shows alert() for runtime errors by default. Since react-unity-webgl v9 removed
// error interception, we override window.alert to prevent disruptive error popups.
// Errors are logged to console for debugging without breaking user experience.
const installUnityAlertSuppression = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const win = window as any;
  if (win.__unityAlertSuppressionInstalled) return;
  win.__unityAlertSuppressionInstalled = true;

  const originalAlert = window.alert;
  window.alert = (message: string) => {
    // Check if this is a Unity runtime error
    const isUnityError =
      message &&
      (message.includes("An error occurred running the Unity content") ||
        message.includes("RuntimeError") ||
        message.includes("indirect call to null") ||
        message.includes("wasm-function"));

    if (isUnityError) {
      // Log to console instead of showing alert
      console.error("[Unity WebGL Error - Suppressed Alert]", message);
      return;
    }

    // For non-Unity alerts, use the original alert function
    originalAlert.call(window, message);
  };
};

export default function ProjectPreviewWebGL({
  projectGroupId = -1,
  projectId = -1,
}: {
  projectGroupId?: number;
  projectId?: number;
}): ReactElement {
  const [isLoading, setIsLoading] = useState(true);
  const jwt = String(useSelector(jwtSelector));
  const isSupportingWebGL = useMemo(() => isBrowserSupportingWebGL(), []);
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const canvasRef = createRef<HTMLCanvasElement>();

  const params = useParams();

  // Story 13.8: Keyboard guard is installed at module level (see unityKeyboardGuard.ts)
  // MainProject.tsx controls it via window.__setUnityKeyboardGuardActive

  // Story 13.8: Memoize Unity config to prevent context recreation during re-renders
  const unityConfig = useMemo(
    () => ({
      loaderUrl: viewerFolder + "Build/ProjectPreviewWebGL.loader.js",
      dataUrl: viewerFolder + "Build/ProjectPreviewWebGL.data",
      frameworkUrl: viewerFolder + "Build/ProjectPreviewWebGL.framework.js",
      codeUrl: viewerFolder + "Build/ProjectPreviewWebGL.wasm",
    }),
    [],
  );

  const {
    unityProvider,
    sendMessage,
    isLoaded,
    loadingProgression,
    UNSAFE__detachAndUnloadImmediate,
  } = useUnityContext(unityConfig);

  // Story 13.5: Safe sendMessage wrapper with error handling
  // Wraps sendMessage calls in try-catch to prevent uncaught errors from
  // propagating and causing the Unity error alert popup.
  const safeSendMessage = (
    gameObjectName: string,
    methodName: string,
    parameter?: string | number,
  ) => {
    try {
      sendMessage(gameObjectName, methodName, parameter);
    } catch (error) {
      console.error(`[Unity sendMessage Error] ${gameObjectName}.${methodName}:`, error);
    }
  };

  const configureUnityApp = () => {
    if (projectId < 0 && projectGroupId < 0) {
      projectId = Number(params.projectId ?? -1);
      projectGroupId = Number(params.id ?? -1);
    }

    // Story 13.5: Use safeSendMessage to prevent uncaught errors from showing alerts
    safeSendMessage("DataManager", "SetProjectGroupID", projectGroupId);
    safeSendMessage("DataManager", "SetProjectID", projectId);
    safeSendMessage("DataManager", "SetBuildID", 0);
    safeSendMessage("DataManager", "SetToken", jwt);

    // Ensure REACT_APP_API_ENDPOINT is defined before sending to Unity
    const apiEndpoint = process.env.REACT_APP_API_ENDPOINT || "";
    if (apiEndpoint) {
      safeSendMessage("ServerConfig", "SetCustomStrapi", apiEndpoint + "/");
    } else {
      console.warn("[Unity Config] REACT_APP_API_ENDPOINT is not defined");
    }

    safeSendMessage("DataManager", "SignalIdSetup");

    canvasRef.current?.setAttribute("tabindex", "1");
    canvasRef.current?.focus();

    setIsLoading(false);
  };

  // Story 13.5: Install Unity alert suppression on component mount
  // This prevents Unity runtime errors from showing disruptive alert() popups
  useEffect(() => {
    installUnityAlertSuppression();
  }, []);

  // Store the cleanup function and loaded state in refs to avoid dependency changes triggering cleanup
  const detachRef = useRef(UNSAFE__detachAndUnloadImmediate);
  const isLoadedRef = useRef(isLoaded);
  useEffect(() => {
    detachRef.current = UNSAFE__detachAndUnloadImmediate;
    isLoadedRef.current = isLoaded;
  }, [UNSAFE__detachAndUnloadImmediate, isLoaded]);

  // Cleanup Unity instance on unmount
  // Story 10.7: Includes keypress recovery handler to compensate for orphaned Emscripten handlers
  // Story 13.8: Only cleanup if Unity actually loaded to prevent GLctx errors during initialization
  useEffect(() => {
    return () => {
      // Only call detach if Unity finished loading - prevents GLctx errors from cleaning up during init
      if (isLoadedRef.current) {
        detachRef.current();
      } else {
        console.log("[Unity Cleanup] Skipping detach - Unity never finished loading");
      }

      // CRITICAL: Protect app from orphaned keypress handlers that call preventDefault()
      // When Unity cleanup runs during loading, Emscripten's keyboard handlers on window
      // may persist and block keyboard input. This recovery handler detects prevented
      // keypress events and manually inserts characters. See Story 10.7 for details.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const globalWin = window as any;
      if (!globalWin.__keypressRecoveryActive) {
        globalWin.__keypressRecoveryActive = true;

        const keypressRecovery = (e: KeyboardEvent) => {
          // Don't process canvas events (let Unity handle those)
          if (e.target instanceof HTMLCanvasElement) return;

          // If default was prevented and we have a printable key, recover by inserting manually
          if (e.defaultPrevented && e.key && e.key.length === 1) {
            const target = e.target as HTMLElement;
            if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
              const start = target.selectionStart || 0;
              const end = target.selectionEnd || 0;
              const value = target.value;
              target.value = value.substring(0, start) + e.key + value.substring(end);
              target.selectionStart = target.selectionEnd = start + 1;
              target.dispatchEvent(new Event("input", { bubbles: true }));
            }
          }
        };

        document.addEventListener("keypress", keypressRecovery, { capture: false });
        globalWin.__keypressRecoveryHandler = keypressRecovery;

        // Auto-remove after 60 seconds (orphaned handlers don't persist forever)
        setTimeout(() => {
          if (globalWin.__keypressRecoveryHandler) {
            document.removeEventListener("keypress", globalWin.__keypressRecoveryHandler, {
              capture: false,
            });
            globalWin.__keypressRecoveryActive = false;
            globalWin.__keypressRecoveryHandler = null;
          }
        }, 60000);
      }
    };
  }, []);

  useEffect(() => {
    dispatch(
      setAlert({
        msg: t("pages.projectGroup.preview.experimentalWarning"),
        type: "warning",
        isOpen: true,
      }),
    );
  }, [jwt, projectGroupId]);

  useEffect(() => {
    if (isLoaded) {
      configureUnityApp();
    }
    console.log("isLoaded ?? ", isLoaded);
  }, [isLoaded]);

  // Handle WebGL context loss events on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const handleContextLost = (e: Event) => {
      e.preventDefault();
    };

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    const handleContextRestored = () => {};

    canvas.addEventListener("webglcontextlost", handleContextLost);
    canvas.addEventListener("webglcontextrestored", handleContextRestored);

    return () => {
      canvas.removeEventListener("webglcontextlost", handleContextLost);
      canvas.removeEventListener("webglcontextrestored", handleContextRestored);
    };
  }, [canvasRef.current]);

  const toggle = () => {
    const elem = document.querySelector("#div-unity");
    const requestFS = () =>
      elem
        ? elem?.requestFullscreen().catch((err) => {
            alert(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
          })
        : null;

    if (!document.fullscreenElement) {
      requestFS();
    } else {
      document.exitFullscreen();
    }
  };

  return (
    <div className="h-3/4 p-2">
      <div
        className={`w-full h-full flex flex-col justify-center items-center ${
          isLoading ? "" : "hidden"
        }`}
      >
        <p>{t("general.loading")}</p>
        <ProgressBar
          wrapperClassName="w-1/4 flex bg-gray-500 h-[3%]"
          barClassName="bg-gray-900"
          value={loadingProgression * 100}
        />
      </div>
      <div id="div-unity" className="h-full flex flex-col items-center">
        {isSupportingWebGL ? (
          <>
            <Unity
              unityProvider={unityProvider}
              ref={canvasRef}
              className={isLoading ? "hidden" : "visible"}
              style={{ height: "100%", width: "100%" }}
            />
          </>
        ) : (
          <UnsupportedWebGL />
        )}
        {isSupportingWebGL && (
          <button
            className={`relative mr-2 mt-[-3rem] self-end btn-fullscreen ${
              isLoading ? "hidden" : "visible"
            }`}
            onClick={() => {
              toggle();
            }}
          >
            <RiFullscreenLine fill="white" size={40} />
          </button>
        )}
      </div>
    </div>
  );
}
